import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { selectAdminToken } from "../../admin/sessionSlice";
import { setBackButtonUrl } from "../uiSlice";
import { GAME_UPLOAD_PATH } from "../../utils/playerNavigation";
import { apiUrl } from "../../utils/apiUrl";
import { categoryIconUrl, s3ThemeAssetUrl } from "../../utils/themeAssets";
import "../../components/stage/stageShell.css";
import "./leaderboard.css";

const REACTIONS = [
  { key: "heart", emoji: "❤️" },
  { key: "smile", emoji: "😄" },
  { key: "clap", emoji: "👏" },
];

function ReactionCounts({ counts }) {
  const c = counts || {};
  return (
    <div className="tlb-reactions">
      {REACTIONS.map((r) => (
        <span key={r.key} className="tlb-reaction">
          <span aria-hidden>{r.emoji}</span>
          <span>{c[r.key] ?? 0}</span>
        </span>
      ))}
    </div>
  );
}

/* ── helpers kept for YogaLeaderboardModal ─────────────────────────────── */

function parseTimeToSeconds(t) {
  if (t == null) return 0;
  if (typeof t === "number" && Number.isFinite(t)) return Math.max(0, Math.floor(t));
  if (typeof t !== "string") return 0;
  const s = String(t).trim();
  if (/^\d+$/.test(s)) return parseInt(s, 10) || 0;
  const parts = s.split(":").map((p) => parseInt(String(p).trim(), 10) || 0);
  if (parts.length >= 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length >= 2) return parts[0] * 60 + parts[1];
  return 0;
}

function pickDisplayScore(m) {
  const keys = ["points", "total_score", "spot_score", "score"];
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    if (!(key in m)) continue;
    const v = m[key];
    if (v == null || v === "") continue;
    const n = parseInt(String(v).trim(), 10);
    if (Number.isFinite(n)) return Math.max(0, n);
  }
  return 0;
}

function pickDisplayTime(m) {
  const raw = m.time ?? m.reportTime;
  if (raw == null || raw === "") return "00:00";
  if (typeof raw === "number" && Number.isFinite(raw)) return String(Math.max(0, Math.floor(raw)));
  return String(raw).trim().slice(0, 50) || "00:00";
}

export function normalizeLeaderboardMatch(m) {
  return { ...m, score: pickDisplayScore(m), time: pickDisplayTime(m) };
}

export function dedupeByUserIdBestScore(rows) {
  const map = new Map();
  for (const r of rows) {
    const id =
      r.userId != null && r.userId !== ""
        ? String(r.userId)
        : `k:${r.email || ""}:${r.name || ""}`;
    const prev = map.get(id);
    const score = r.score ?? 0;
    if (!prev || score > (prev.score ?? 0)) {
      map.set(id, r);
    } else if (prev && score === (prev.score ?? 0)) {
      const ta = parseTimeToSeconds(String(r.time));
      const tb = parseTimeToSeconds(String(prev.time));
      if (ta < tb) map.set(id, r);
    }
  }
  return [...map.values()];
}

/* ── talent leaderboard ────────────────────────────────────────────────── */

function firstEntry(obj) {
  if (!obj || typeof obj !== "object") return [null, null];
  const k = Object.keys(obj)[0];
  return [k, obj[k]];
}

function buildCategoryIconMap(themeData) {
  const names = Array.isArray(themeData?.default_category_name)
    ? themeData.default_category_name
    : [];
  const icons = Array.isArray(themeData?.category_icons) ? themeData.category_icons : [];
  const map = {};
  names.forEach((name, i) => {
    if (String(name).toUpperCase() === "ALL") return;
    const [, iconVal] = firstEntry(icons[i]);
    if (name) {
      map[String(name).trim().toLowerCase()] = String(iconVal || "").trim();
    }
  });
  return map;
}

function formatRank(rank) {
  const n = Number(rank) || 0;
  return n < 10 ? `0${n}` : String(n);
}

function entrySubtitle(entry) {
  return String(entry.message || entry.category || "").trim();
}

function RankRibbon({ rank }) {
  const tone =
    rank === 1 ? "gold" : rank === 2 ? "purple" : rank === 3 ? "bronze" : "default";
  return (
    <div className={`tlb-ribbon tlb-ribbon--${tone}`} aria-hidden>
      <span>{rank}</span>
    </div>
  );
}

function CategoryIcon({ src }) {
  return (
    <img
      className="tlb-cat-icon"
      src={categoryIconUrl(src)}
      alt=""
      referrerPolicy="no-referrer"
    />
  );
}

function formatResultScore(entry) {
  const n = Number(entry?.weightedScore);
  if (!Number.isFinite(n)) return "0";
  return Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/\.?0+$/, "");
}

function PodiumCard({ entry, icon }) {
  const tier = entry.rank === 1 ? 1 : entry.rank === 2 || entry.rank === 3 ? 2 : 3;
  const subtitle = entrySubtitle(entry);
  return (
    <article className={`tlb-podium-card tlb-podium-card--tier-${tier}`}>
      <RankRibbon rank={entry.rank} />
      <div className="tlb-podium-avatar">
        <CategoryIcon src={icon} name={entry.category || entry.name} />
      </div>
      <h3 className="tlb-podium-name">{entry.name || "Anonymous"}</h3>
      {subtitle ? <p className="tlb-podium-sub">{subtitle}</p> : null}
      <div className="tlb-vote-box">
        {formatResultScore(entry)} PTS
      </div>
      <ReactionCounts counts={entry.reactionCounts} />
    </article>
  );
}

export default function LeaderboardLayout() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const themeData = useSelector((s) => s.theme.data);
  const user = useSelector((s) => s.auth.user);
  const adminToken = useSelector(selectAdminToken);

  const iconMap = useMemo(() => buildCategoryIconMap(themeData), [themeData]);
  const logoSrc = themeData?.logo ? s3ThemeAssetUrl(themeData.logo) : "";

  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState("");

  useEffect(() => {
    dispatch(setBackButtonUrl(GAME_UPLOAD_PATH));
  }, [dispatch]);

  const load = useCallback(async () => {
    if (!adminToken) {
      setLoading(false);
      setFetchError("Missing session.");
      return;
    }
    setLoading(true);
    setFetchError("");
    try {
      const resp = await fetch(apiUrl("/talentLeaderboard"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify({
          userId: user?.userId || "",
        }),
      });
      const payload = await resp.json();
      if (!resp.ok || !payload?.success) {
        throw new Error(payload?.error || "Could not load leaderboard");
      }
      setEntries(Array.isArray(payload.entries) ? payload.entries : []);
    } catch (e) {
      setEntries([]);
      setFetchError(e.message || "Could not load leaderboard");
    } finally {
      setLoading(false);
    }
  }, [adminToken, user?.userId]);

  useEffect(() => {
    load();
  }, [load]);

  const TOP_N = 5;

  const displayEntries = useMemo(() => {
    const topN = entries.slice(0, TOP_N);
    const myUserId = user?.userId != null ? String(user.userId) : "";
    const myEmail =
      user?.email != null ? String(user.email).trim().toLowerCase() : "";

    const isMine = (e) => {
      if (myUserId && e.userId != null && String(e.userId) === myUserId) return true;
      if (myEmail && e.email && String(e.email).trim().toLowerCase() === myEmail)
        return true;
      return false;
    };

    // Best-ranked upload for the current user (lowest rank number).
    const mine = entries.filter(isMine).sort((a, b) => a.rank - b.rank)[0];
    const inTopN =
      !!mine && topN.some((e) => String(e.id) === String(mine.id));

    if (mine && !inTopN) return [...topN, mine];
    return topN;
  }, [entries, user?.userId, user?.email]);

  const topThree = displayEntries.slice(0, 3);
  const rest = displayEntries.slice(3);
  const podiumOrder = useMemo(() => {
    const byRank = new Map(topThree.map((e) => [e.rank, e]));
    return [2, 1, 3].map((r) => byRank.get(r)).filter(Boolean);
  }, [topThree]);

  const iconFor = (entry) =>
    iconMap[String(entry.category || "").trim().toLowerCase()] || "";

  return (
    <div className="stage-shell tlb-page">
      <div className="stage-inner">
        <div className="tlb-topbar">
          <button
            type="button"
            className="tlb-gallery-btn"
            onClick={() => navigate("/gallery")}
          >
            ← Gallery
          </button>
        </div>

        <header className="tlb-header">
          {logoSrc ? (
            <img
              className="tlb-logo"
              src={logoSrc}
              alt="We Got Talent"
              referrerPolicy="no-referrer"
            />
          ) : null}
          <h1 className="tlb-title">Leaderboard</h1>
          <p className="tlb-subtitle">
            Ranked by jury + likes (jury weight from admin settings)
          </p>
        </header>

        {loading ? (
          <div className="tlb-loading">
            <div className="quiz-loader" />
            <span>Loading rankings…</span>
          </div>
        ) : fetchError ? (
          <div className="tlb-empty tlb-empty--error">
            <p>{fetchError}</p>
            <button type="button" className="stage-cta" onClick={load}>
              Retry
            </button>
          </div>
        ) : entries.length === 0 ? (
          <div className="tlb-empty">
            <p>No votes yet. Be the first to vote in the gallery!</p>
          </div>
        ) : (
          <>
            <div className="tlb-podium" role="list" aria-label="Top three">
              {podiumOrder.map((entry) => (
                <div key={entry.id} role="listitem">
                  <PodiumCard entry={entry} icon={iconFor(entry)} />
                </div>
              ))}
            </div>

            {rest.length > 0 ? (
              <div className="tlb-list-panel">
                <ul className="tlb-list">
                  {rest.map((entry) => {
                    const subtitle = entrySubtitle(entry);
                    const isMe =
                      (user?.userId != null &&
                        entry.userId != null &&
                        String(entry.userId) === String(user.userId)) ||
                      (user?.email &&
                        entry.email &&
                        String(entry.email).trim().toLowerCase() ===
                          String(user.email).trim().toLowerCase());
                    return (
                      <li
                        key={entry.id}
                        className={`tlb-row${isMe ? " tlb-row--me" : ""}`}
                      >
                        <span className="tlb-row-rank">{formatRank(entry.rank)}</span>
                        <CategoryIcon
                          src={iconFor(entry)}
                          name={entry.category || entry.name}
                        />
                        <div className="tlb-row-meta">
                          <span className="tlb-row-name">
                            {entry.name || "Anonymous"}
                          </span>
                          {subtitle ? (
                            <span className="tlb-row-cat">{subtitle}</span>
                          ) : null}
                        </div>
                        <ReactionCounts counts={entry.reactionCounts} />
                        <span className="tlb-row-votes">
                          {formatResultScore(entry)} PTS
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : null}
          </>
        )}
      </div>
      {/* <div className="stage-platform" aria-hidden /> */}
    </div>
  );
}
